import { useMemo } from 'react'

import { matchFilterString, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'
import type { UseLoadMorePayload } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import { SystemModel } from '@models/types'

import type { EntityOptions } from '@redux/modules/types'

const watchEntityKeys = ['systems']

export type SystemsFilters = {
  string?: string,
}

interface UseSystemsOptions {
  entityKey?: string,
  filters?: SystemsFilters,
  performHttpRequests?: boolean,
  requestOptions?: EntityOptions,
}

function useSystems(options: UseSystemsOptions = {}) {
  const { filters } = options
  const {
    string: filterString,
  } = filters || {}

  const {
    updatedEntities: { systems: systemsUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const systems = useSelector(reduxState => reduxState.entities.systems)

  const filteredSystems = useMemo((): SystemModel[] => {
    const filtered = Object.values(systems).filter((system) => {
      const { id, title } = system

      const matchId = id.toString() === filterString
      const matchTitle = matchFilterString(title, filterString)
      const matchString = matchId || matchTitle

      return matchString
    })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [systemsUpdatedAt, JSON.stringify(filters)])

  const filteredSystemsCount = filteredSystems.length
  const hasFilteredSystems = !!filteredSystemsCount

  const loadMorePayload: UseLoadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredSystemsCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingSystems } = useReduxAction(
    'systems',
    'loadSystems',
    {
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      // @ts-expect-error TS2339
      shouldPerformFn: ({ loading }) => !!performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredSystems,
    filteredSystemsCount,
    hasFilteredSystems,
    loading: loadingSystems,
  }
}

export default useSystems
